import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  InvestigacionSiniestro,
  InvestigacionSiniestroService,
} from "../../../../../shared/services/investigacion-siniestro.service";
import {
  CausaSiniestro,
  CausasSiniestroService,
} from "../../../../../shared/services/causas-siniestro.service";
import {
  PlanAccionSiniestro,
  PlanAccionSiniestroService,
} from "../../../../../shared/services/plan-accion-siniestro.service";
import {
  LeccionAprendida,
  LeccionesAprendidasService,
} from "../../../../../shared/services/lecciones-aprendidas.service";
import {
  SeguimientoPlanAccion,
  SeguimientoPlanAccionService,
} from "../../../../../shared/services/seguimiento-siniestro-plan-accion.service";
import {
  EvaluacionEficacia,
  EvaluacionEficaciaService,
} from "../../../../../shared/services/evaluacion-eficacia.service";
import { SiniestrosService } from "../../../../../shared/services/siniestrosViales.service";
import { PropietarioService } from "../../../../../shared/services/propietarios.service";
import { catchError, of } from "rxjs";
import { Router } from "@angular/router";

@Component({
  selector: "app-investigacion",
  templateUrl: "./investigacion.component.html",
  styleUrls: ["./investigacion.component.scss"],
})
export class InvestigacionComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  investigacionForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idInvestigacionEditando: number | null = null;

  siniestrosActivos: any[] = [];
  cargandoSiniestros = false;

  investigaciones: InvestigacionSiniestro[] = [];
  investigacionesFiltradas: InvestigacionSiniestro[] = [];
  cargandoListado = false;
  filtro = "";
  filtroEstado: number | null = null;
  page = 1;
  pageSize = 10;

  readonly Number = Number;

  // ============ MODAL DE GESTIÓN (causas / plan de acción / lecciones) ============
  investigacionGestionando: InvestigacionSiniestro | null = null;
  gestionTab = 1;

  // --- Causas (form que alterna crear/editar, como en Seguimiento Comportamiento Vial) ---
  causaForm!: FormGroup;
  causas: CausaSiniestro[] = [];
  cargandoCausas = false;
  guardandoCausa = false;
  modoEdicionCausa = false;
  idCausaEditando: number | null = null;
  colaboradores: any[] = [];

  // --- Plan de acción ---
  planAccionForm!: FormGroup;
  planAccion: PlanAccionSiniestro[] = [];
  cargandoPlanAccion = false;
  guardandoPlanAccion = false;
  modoEdicionPlanAccion = false;
  idPlanAccionEditando: number | null = null;

  // --- Lecciones aprendidas ---
  leccionForm!: FormGroup;
  lecciones: LeccionAprendida[] = [];
  cargandoLecciones = false;
  guardandoLeccion = false;
  modoEdicionLeccion = false;
  idLeccionEditando: number | null = null;
  cargandoColaboradores = false;

  // ============ MODAL DE SEGUIMIENTO / EVALUACIÓN (bitácora de una acción) ============
  accionSeleccionada: PlanAccionSiniestro | null = null;

  seguimientoForm!: FormGroup;
  seguimientos: SeguimientoPlanAccion[] = [];
  cargandoSeguimientos = false;
  guardandoSeguimiento = false;
  modoEdicionSeguimiento = false;
  idSeguimientoEditando: number | null = null;

  evaluacionForm!: FormGroup;
  evaluaciones: EvaluacionEficacia[] = [];
  cargandoEvaluaciones = false;
  guardandoEvaluacion = false;
  modoEdicionEvaluacion = false;
  idEvaluacionEditando: number | null = null;

  opcionesEstadoAccion = ["PENDIENTE", "ENCURSO", "CERRADA", "VENCIDA"];

  constructor(
    private fb: FormBuilder,
    private siniestrosService: SiniestrosService,
    private propietarioService: PropietarioService,
    private investigacionService: InvestigacionSiniestroService,
    private evaluacionService: EvaluacionEficaciaService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.inicializarFormularios();
    this.cargarSiniestrosActivos();
    this.cargarColaboradores();
    this.cargarInvestigaciones();
  }

  private inicializarFormularios(): void {
    this.investigacionForm = this.fb.group({
      id_siniestro: [null, Validators.required],
      metodologia: ["", Validators.required],
      responsable_investigacion: [null, Validators.required],
      fecha_inicio: ["", Validators.required],
      entrevistas_realizadas: [""],
      evidencia_recolectada: [""],
      reconstruccion_hechos: [""],
      conclusiones: [""],
    });
  }

  // ============ Catálogo de siniestros (módulo Vehículos) ============
  cargarSiniestrosActivos(): void {
    this.cargandoSiniestros = true;
    this.siniestrosService.getSiniestrosActivos().subscribe({
      next: (data) => {
        this.siniestrosActivos = data;
        this.cargandoSiniestros = false;
      },
      error: () => {
        this.siniestrosActivos = [];
        this.cargandoSiniestros = false;
      },
    });
  }

  // ============ Listado de investigaciones ============
  cargarInvestigaciones(): void {
    this.cargandoListado = true;
    this.investigacionService.getAll().subscribe({
      next: (data) => {
        this.investigaciones = data;
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.investigaciones = [];
        this.investigacionesFiltradas = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.investigacionesFiltradas = this.investigaciones.filter((inv: any) => {
      const coincideTexto =
        !termino ||
        [inv.metodologia, inv.responsable_investigacion, inv.SiniestroData?.consecutivo]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideEstado = this.filtroEstado === null || inv.estado === this.filtroEstado;
      return coincideTexto && coincideEstado;
    });
    this.page = 1;
  }

  // ============ Guardar / editar investigación ============
  guardar(): void {
    if (this.investigacionForm.invalid) {
      this.investigacionForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    const payload = this.investigacionForm.getRawValue();
    this.guardando = true;

    const obs =
      this.modoEdicion && this.idInvestigacionEditando
        ? this.investigacionService.update(this.idInvestigacionEditando, payload)
        : this.investigacionService.create(payload);

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Investigación guardada correctamente.", "success");
        this.cancelarEdicion();
        this.cargarInvestigaciones();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire("Error", error?.error?.message || "No se pudo guardar la investigación.", "error");
      },
    });
  }

  editar(investigacion: any): void {
    this.modoEdicion = true;
    this.idInvestigacionEditando = investigacion.id;
    this.investigacionForm.patchValue({
      id_siniestro: investigacion.id_siniestro,
      metodologia: investigacion.metodologia,
      responsable_investigacion: investigacion.responsable_investigacion,
      fecha_inicio: investigacion.fecha_inicio?.substring(0, 10),
      entrevistas_realizadas: investigacion.entrevistas_realizadas,
      evidencia_recolectada: investigacion.evidencia_recolectada,
      reconstruccion_hechos: investigacion.reconstruccion_hechos,
      conclusiones: investigacion.conclusiones,
    });
    this.activeTab = 1;
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idInvestigacionEditando = null;
    this.investigacionForm.reset();
  }

  cerrarInvestigacion(investigacion: any): void {
    Swal.fire({
      title: "¿Cerrar investigación?",
      text: "Se marcará como cerrada con la fecha de hoy.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, cerrar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.investigacionService.cerrar(investigacion.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Investigación cerrada correctamente.", "success");
          this.cargarInvestigaciones();
        },
        error: () => Swal.fire("Error", "No se pudo cerrar la investigación.", "error"),
      });
    });
  }

  inactivar(investigacion: any): void {
    Swal.fire({
      title: "¿Inactivar investigación?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.investigacionService.inactivar(investigacion.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Investigación inactivada.", "success");
          this.cargarInvestigaciones();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar la investigación.", "error"),
      });
    });
  }


  private idInvestigacionActual(): number {
    return Number(this.investigacionGestionando?.id);
  }


  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  private cargarColaboradores(): void {
    this.cargandoColaboradores = true;
  
    this.propietarioService
      .getPlanAccion()
      .pipe(
        catchError(() => of([]))
      )
      .subscribe({
        next: (response: any) => {
          this.colaboradores = this.normalizarLista(response).map(
            (colaborador: any) => ({
              ...colaborador,
              nombreCompleto: colaborador.nombreCompleto ?? '',
              cargo: colaborador.cargoData?.cargos ?? ''
            })
          );
  
          this.cargandoColaboradores = false;
        },
        error: () => {
          this.colaboradores = [];
          this.cargandoColaboradores = false;
        }
      });
  }
  
    private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

    Gestion(Investigacion: any): void {
    Swal.fire({
      title: "Gestión de Investigación",
      text: `¿Desea ver la gestión para la investigación ${Investigacion.id}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, Ver",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate(["listado/gestión-investigacción/", Investigacion.id]);
      }
    });
  }
}